import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getServiceBySlug } from '../utils/api';
import { decodeHTMLEntities } from '../utils/helpers';
import NotFoundPage from './NotFoundPage';

export default function ServiceDetailPage() {
    const { slug } = useParams();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadService = async () => {
            if (!slug) {
                setLoading(false);
                return;
            }
            const data = await getServiceBySlug(slug);
            if (data) {
                const sm = data.service_meta || data.meta || {};
                setService({
                    title: decodeHTMLEntities(data.title.rendered),
                    content: data.content.rendered,
                    icon: sm.icon || sm.service_icon || '🏗️',
                    tags: sm.tags ? sm.tags.split(',') : (sm.service_tags ? sm.service_tags.split(',') : [])
                });
            }
            setLoading(false);
        };
        loadService();
    }, [slug]);

    if (loading) {
        return (
            <div className="pt-32 px-6 sm:px-8 flex items-center justify-center min-h-screen">
                <div className="w-12 h-12 border-4 border-nocturne-purple border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!service) {
        return <NotFoundPage />;
    }

    return (
        <div className="pt-16 min-h-screen">
            {/* Header Hero */}
            <section className="py-20 px-6 sm:px-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none opacity-50" />
                <div className="max-w-4xl mx-auto relative z-10">
                    <Link
                        to="/services"
                        className="text-nocturne-muted hover:text-nocturne-text transition-colors text-sm mb-12 flex items-center gap-2"
                    >
                        ← Back to Services
                    </Link>

                    <div className="flex flex-col md:flex-row gap-8 items-start">
                        <div className="w-20 h-20 rounded-2xl bg-nocturne-purple/10 border border-nocturne-purple/30 flex items-center justify-center text-5xl shrink-0 overflow-hidden">
                            {service.icon}
                        </div>
                        <div>
                            <p className="section-tag mb-2">SERVICE DETAIL</p>
                            <h1 className="text-5xl md:text-6xl font-black text-nocturne-text leading-tight mb-6">
                                {service.title}
                            </h1>
                            <div className="flex flex-wrap gap-2">
                                {service.tags.map((tag, i) => (
                                    <span key={i} className="px-3 py-1 rounded-full border border-nocturne-border text-nocturne-muted text-xs font-semibold">
                                        • {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16 px-6 sm:px-8 relative">
                <div className="max-w-4xl mx-auto">
                    <div className="card border-nocturne-purple/20 bg-nocturne-card/30 backdrop-blur-md p-8 md:p-12">
                        <div
                            className="prose prose-invert prose-purple max-w-none text-nocturne-muted leading-relaxed text-lg 
                                       [&>p]:mb-6 [&>h2]:text-nocturne-text [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mt-10 [&>h2]:mb-4"
                            dangerouslySetInnerHTML={{ __html: service.content }}
                        />
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="py-24 px-6 sm:px-8">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-4xl font-black text-nocturne-text mb-6">
                        Ready to integrate <span className="gradient-text">{service.title}</span>?
                    </h2>
                    <p className="text-nocturne-muted mb-10 max-w-md mx-auto">
                        Our architects are standing by to help you scale your digital infrastructure with precision.
                    </p>
                    <Link to="/contact" className="btn-primary inline-block px-10 py-4 text-base">
                        Start Your Build →
                    </Link>
                </div>
            </section>
        </div>
    );
}
